export const PROPERTY_BOUNDARY_REQUIRED_MESSAGE =
  'A property needs a boundary. Add a parcel or draw a boundary first, or delete the property instead.'

export const PROPERTY_BOUNDARY_REQUIRED_ON_CREATE_MESSAGE =
  'Select at least one parcel or draw a custom boundary before saving.'

export type PropertyBoundary = {
  propertyId: string | null
  geometry: Record<string, unknown>
  lineColor: string | null
  areaColor: string | null
  lineStyle: 'solid' | 'dashed' | 'dotted' | null
  lineWeight: 1 | 2 | 3 | null
  dissolved?: boolean
}

export type OwnedParcel = {
  robustId: string
  lineColor: string | null
  areaColor: string | null
  lineStyle: 'solid' | 'dashed' | 'dotted' | null
  lineWeight: 1 | 2 | 3 | null
  drawnByPropertyBoundary?: boolean
}

export type PropertyBoundarySource = {
  id: string
  customBoundary?: Record<string, unknown> | null
  boundaryGeometry?: Record<string, unknown> | null
  boundaryLineColor?: string | null
  boundaryAreaColor?: string | null
  boundaryLineStyle?: 'solid' | 'dashed' | 'dotted' | null
  boundaryLineWeight?: 1 | 2 | 3 | null
  attachedRobustIds?: string[] | null
}

export type ParcelBoundarySource = {
  propertyId?: string | null
  parcelExternalId?: string | null
  geometry?: Record<string, unknown> | null
  lineColor?: string | null
  lineStyle?: 'solid' | 'dashed' | 'dotted' | null
  lineWeight?: 1 | 2 | 3 | null
}

export function hasDissolvedBoundary(property: PropertyBoundarySource): boolean {
  const geometry = property.boundaryGeometry as
    | { type?: unknown; coordinates?: unknown }
    | null
    | undefined
  if (geometry == null || typeof geometry !== 'object') return false
  if (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon') return false
  return Array.isArray(geometry.coordinates) && geometry.coordinates.length > 0
}

function toBoundary(
  property: PropertyBoundarySource,
  geometry: Record<string, unknown>,
  dissolved: boolean,
): PropertyBoundary {
  return {
    propertyId: property.id,
    geometry,
    lineColor: property.boundaryLineColor ?? null,
    areaColor: property.boundaryAreaColor ?? null,
    lineStyle: property.boundaryLineStyle ?? null,
    lineWeight: property.boundaryLineWeight ?? null,
    dissolved,
  }
}

export function buildPropertyBoundaries(
  properties: PropertyBoundarySource[],
  parcelFeatures: ParcelBoundarySource[],
): PropertyBoundary[] {
  const propertyById = new Map(properties.map((property) => [property.id, property]))
  const result: PropertyBoundary[] = []

  for (const property of properties) {
    const custom = property.customBoundary
    if (custom && typeof custom === 'object') {
      result.push(toBoundary(property, custom, false))
    }
    if (hasDissolvedBoundary(property)) {
      result.push(
        toBoundary(property, property.boundaryGeometry as Record<string, unknown>, true),
      )
    }
  }

  for (const feature of parcelFeatures) {
    if (!feature.geometry || typeof feature.geometry !== 'object') continue
    const owner = feature.propertyId ? propertyById.get(feature.propertyId) : undefined
    if (owner && hasDissolvedBoundary(owner)) continue
    result.push({
      propertyId: feature.propertyId ?? null,
      geometry: feature.geometry,
      lineColor: owner?.boundaryLineColor ?? feature.lineColor ?? null,
      areaColor: owner?.boundaryAreaColor ?? null,
      lineStyle: owner?.boundaryLineStyle ?? feature.lineStyle ?? null,
      lineWeight: owner?.boundaryLineWeight ?? feature.lineWeight ?? null,
      dissolved: false,
    })
  }

  return result
}

export function dissolvedParcelRobustIds(
  properties: PropertyBoundarySource[],
  parcelFeatures: ParcelBoundarySource[],
): Set<string> {
  const dissolvedPropertyIds = new Set(
    properties.filter((property) => hasDissolvedBoundary(property)).map((property) => property.id),
  )
  const result = new Set<string>()
  for (const feature of parcelFeatures) {
    if (!feature.propertyId || !feature.parcelExternalId) continue
    if (!dissolvedPropertyIds.has(feature.propertyId)) continue
    result.add(feature.parcelExternalId)
  }
  return result
}

export function buildOwnedParcels(
  properties: PropertyBoundarySource[],
  parcelFeatures: ParcelBoundarySource[],
): OwnedParcel[] {
  const dissolvedIds = dissolvedParcelRobustIds(properties, parcelFeatures)
  const result: OwnedParcel[] = []
  for (const property of properties) {
    for (const robustId of property.attachedRobustIds ?? []) {
      result.push({
        robustId,
        lineColor: property.boundaryLineColor ?? null,
        areaColor: property.boundaryAreaColor ?? null,
        lineStyle: property.boundaryLineStyle ?? null,
        lineWeight: property.boundaryLineWeight ?? null,
        drawnByPropertyBoundary: dissolvedIds.has(robustId),
      })
    }
  }
  return result
}
